import { useContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import HomePage from '../../pages/HomePage';
import LoginPage from '../../pages/Account/loginPage';
import RegisterPage from '../../pages/Account/registerPage';
import ForgotPasswordPage from '../../pages/Account/ForgotPasswordPage';
import ResetPasswordPage from '../../pages/Account/ResetPasswordPage';
import MyRelative from '../../pages/MyRelative/MyRelative';
import Profile from '../../pages/Profile/Profile';
import HomeElderly from '../../pages/HomePages/HomeElderly';
import HomeRelative from '../../pages/HomePages/HomeRelative';
import MedicalHistory from '../../pages/Medical/MedicalHistory';
import HealthMonitor from '../../pages/HealthMonitor';
import MedicationChecklist from '../../pages/MedicationChecklist';
import MedicationChecklistSelect from '../../pages/MedicationChecklistSelect';
import ChatBox from '../ChatBox';
import AdminDashboard from '../../pages/Admin/AdminDashboard';
import AdminUsers from '../../pages/Admin/AdminUsers';
import AdminHealthLogs from '../../pages/Admin/AdminHealthLogs';
import AdminAlerts from '../../pages/Admin/AdminAlerts';
import AlertsPage from '../../pages/AlertsPage';
import RevisitAppointmentPage from '../../pages/RevisitAppointmentPage';
import BlogList from '../../pages/BlogList';
import BlogDetail from '../../pages/BlogDetail';
import BlogExternalDetail from '../../pages/BlogExternalDetail';
import AdminBlogs from '../../pages/Admin/AdminBlogs';
import AdminShop from '../../pages/Admin/AdminShop';
import AdminShopCategories from '../../pages/Admin/AdminShopCategories';
import AdminShopProducts from '../../pages/Admin/AdminShopProducts';
import ProductsPage from '../../pages/Shop/ProductsPage';
import ProductDetailPage from '../../pages/Shop/ProductDetailPage';
import CartPage from '../../pages/Shop/CartPage';
import CheckoutPage from '../../pages/Shop/CheckoutPage';
import MyOrdersPage from '../../pages/Shop/MyOrdersPage';
import PrivacyPolicy from '../../pages/Legal/PrivacyPolicy';
import TermsOfService from '../../pages/Legal/TermsOfService';
import DeleteAccountPage from '../../pages/Account/DeleteAccountPage';
import MembershipPage from '../../pages/Membership/MembershipPage';
import { DataContext } from '../../Provider/dataProvider';
import FeatureGate from '../FeatureGate';
import { FEATURES } from '../../constants/featureAccess';


const NotFound = () => (
    <div className="container" style={{ padding: '96px 24px', textAlign: 'center' }}>
        <h1 style={{ color: 'var(--primary)', marginBottom: '12px' }}>Không tìm thấy trang</h1>
        <p style={{ color: 'var(--text-muted)' }}>Đường dẫn này chưa được cấu hình trong client hiện tại.</p>
    </div>
);

const AccessGate = ({ message = 'Đang kiểm tra quyền truy cập...' }) => (
    <div className="container" style={{ padding: '96px 24px', textAlign: 'center' }}>
        <h1 style={{ color: 'var(--primary)', marginBottom: '12px' }}>{message}</h1>
        <p style={{ color: 'var(--text-muted)' }}>Vui lòng đợi trong giây lát.</p>
    </div>
);

const roleHomeMap = {
    elderly: '/home_elderly',
    relative: '/home_caregiver',
    admin: '/admin/dashboard',
};

const AuthGuard = ({ children, roles }) => {
    const { data, accessToken, authReady } = useContext(DataContext);
    const location = useLocation();
    const role = String(data?.role || '').toLowerCase();

    if (!authReady) {
        return <AccessGate message="Đang xác thực phiên đăng nhập..." />;
    }

    if (!accessToken || !data?.id) {
        return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }

    if (roles?.length && !roles.includes(role)) {
        return <Navigate to={roleHomeMap[role] || '/login'} replace />;
    }

    return children;
};

const AdminGuard = ({ children }) => {
    const { data, authReady } = useContext(DataContext);
    const location = useLocation();
    const role = String(data?.role || '').toLowerCase();

    if (!authReady) {
        return <AccessGate message="Đang xác thực phiên đăng nhập..." />;
    }

    if (role !== 'admin') {
        return <Navigate to={roleHomeMap[role] || '/login'} replace state={{ from: location }} />;
    }

    return children;
};

const MainContent = () => {
    const LegacyBlogRedirect = () => {
        const location = useLocation();
        const params = new URLSearchParams(location.search);
        const id = params.get('id');
        return <Navigate to={id ? `/blogs/${id}` : '/blogs'} replace />;
    };
    return (
        <>
            <div className='mainContent'>
                <ChatBox />
                <Routes>

                    <Route path="/" element={<HomePage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/account/delete" element={<DeleteAccountPage />} />
                    <Route path="/my-relatives" element={<MyRelative />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/profile/:accountId" element={<Profile />} />
                    <Route path="/membership" element={<MembershipPage />} />
                    <Route path="/home_elderly" element={<AuthGuard roles={['elderly']}><HomeElderly /></AuthGuard>} />
                    <Route path="/home_caregiver" element={<AuthGuard roles={['relative']}><HomeRelative /></AuthGuard>} />
                    <Route path="/medical-history/:profileId" element={<FeatureGate feature={FEATURES.MEDICAL_RECORDS} skipPerElderlyCheck><MedicalHistory /></FeatureGate>} />
                    <Route path="/medical-history" element={<FeatureGate feature={FEATURES.MEDICAL_RECORDS} skipPerElderlyCheck><MedicalHistory /></FeatureGate>} />
                    <Route path="/alerts" element={<FeatureGate feature={FEATURES.ALERTS}><AlertsPage /></FeatureGate>} />
                    <Route path="/revisit-appointment/:recordId" element={<RevisitAppointmentPage />} />
                    <Route path="/blogs" element={<BlogList />} />
                    <Route path="/blogs/:blogId" element={<BlogDetail />} />
                    <Route path="/blogs/external" element={<BlogExternalDetail />} />
                    <Route path="/blog" element={<LegacyBlogRedirect />} />
                    <Route path="/health/:elderlyId" element={<FeatureGate feature={FEATURES.HEALTH_MONITOR}><HealthMonitor /></FeatureGate>} />
                    <Route path="/health" element={<FeatureGate feature={FEATURES.HEALTH_MONITOR}><HealthMonitor /></FeatureGate>} />
                    <Route path="/health-monitor/:elderlyId" element={<FeatureGate feature={FEATURES.HEALTH_MONITOR}><HealthMonitor /></FeatureGate>} />
                    <Route path="/health-monitor" element={<FeatureGate feature={FEATURES.HEALTH_MONITOR}><HealthMonitor /></FeatureGate>} />
                    <Route path="/elderly-calendar/:elderlyId" element={<FeatureGate feature={FEATURES.HEALTH_MONITOR}><HealthMonitor /></FeatureGate>} />
                    <Route path="/medication-checklist/:elderlyId" element={<FeatureGate feature={FEATURES.MEDICATION}><MedicationChecklist /></FeatureGate>} />
                    <Route path="/medication-checklist" element={<FeatureGate feature={FEATURES.MEDICATION}><MedicationChecklistSelect /></FeatureGate>} />
                    <Route path="/elderly-calendar" element={<FeatureGate feature={FEATURES.HEALTH_MONITOR}><HealthMonitor /></FeatureGate>} />
                    <Route path="/admin" element={<AdminGuard><Navigate to="/admin/dashboard" replace /></AdminGuard>} />
                    <Route path="/admin/dashboard" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
                    <Route path="/admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
                    <Route path="/admin/health-logs" element={<AdminGuard><AdminHealthLogs /></AdminGuard>} />
                    <Route path="/admin/alerts" element={<AdminGuard><AdminAlerts /></AdminGuard>} />
                    <Route path="/admin/blogs" element={<AdminGuard><AdminBlogs /></AdminGuard>} />
                    <Route path="/admin/shop" element={<AdminGuard><AdminShop /></AdminGuard>} />
                    <Route path="/admin/shop/categories" element={<AdminGuard><AdminShopCategories /></AdminGuard>} />
                    <Route path="/admin/shop/products" element={<AdminGuard><AdminShopProducts /></AdminGuard>} />
                    <Route path="/shop/products" element={<FeatureGate feature={FEATURES.SHOP}><ProductsPage /></FeatureGate>} />
                    <Route path="/shop/product/:id" element={<FeatureGate feature={FEATURES.SHOP}><ProductDetailPage /></FeatureGate>} />
                    <Route path="/shop/cart" element={<FeatureGate feature={FEATURES.SHOP}><CartPage /></FeatureGate>} />
                    <Route path="/shop/checkout" element={<FeatureGate feature={FEATURES.SHOP}><CheckoutPage /></FeatureGate>} />
                    <Route path="/shop/orders" element={<FeatureGate feature={FEATURES.SHOP}><MyOrdersPage /></FeatureGate>} />
                    <Route path="*" element={<NotFound />} /> {/* Bắt tất cả các đường dẫn không hợp lệ */}
                </Routes>
            </div>
        </>

    )
}
export default MainContent;